(function () {
  if (window.PuristGiftGuardLoaded) return;
  window.PuristGiftGuardLoaded = true;

  const CONFIG = {
    giftTitlePattern: /scalp\s*scrubber\s*\(gift\)|scalp\s*scrubber|scrubber\s*\(gift\)/i,
    minimumBundleItems: 2,
    checkDelay: 100
  };

  let checking = false;
  let removingBundle = false;
  let timer = null;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async function fetchCart() {
    const response = await fetch(rootUrl() + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  async function changeLineQuantity(lineKey, quantity) {
    const response = await fetch(rootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: String(lineKey),
        quantity: Number(quantity)
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Unable to update cart.');
    }

    return response.json();
  }

  async function updateCartLines(updates) {
    const response = await fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Unable to update cart.');
    }

    return response.json();
  }

  function getProperties(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function getBundleId(item) {
    return getProperties(item)._purist_bundle_id || '';
  }

  function getBundleRole(item) {
    return getProperties(item)._purist_bundle_role || '';
  }

  function itemTitle(item) {
    return [
      item && item.product_title,
      item && item.variant_title,
      item && item.title
    ].filter(Boolean).join(' ');
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isGift(item) {
    const role = getBundleRole(item);
    const title = itemTitle(item);
    const price = Number(
      typeof item.final_price !== 'undefined'
        ? item.final_price
        : typeof item.price !== 'undefined'
          ? item.price
          : 0
    );

    return role === 'free_gift' || (price === 0 && CONFIG.giftTitlePattern.test(title));
  }

  function isQualifyingBundleItem(item) {
    const role = getBundleRole(item);
    return Boolean(getBundleId(item) && role && role !== 'free_gift');
  }

  function countBundleItemsByBundleId(cart) {
    const counts = new Map();

    (cart.items || []).forEach((item) => {
      if (!isQualifyingBundleItem(item)) return;

      const bundleId = getBundleId(item);
      const current = counts.get(bundleId) || 0;
      counts.set(bundleId, current + Number(item.quantity || 0));
    });

    return counts;
  }

  function updateCartCount(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    const count = Number(cart.item_count || 0);
    const countText = String(count);

    const countSelectors = [
      '.m-cart-count-bubble',
      '.m-cart-count',
      '[data-cart-count]',
      '.cart-count-bubble',
      '.cart-count',
      '[data-testid="cart-count"]',
      '.ocu-cart-header--items-count span'
    ];

    countSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        /*
          Do not rewrite the same text value. Setting textContent creates a
          childList mutation, which can cause the guard to trigger itself.
        */
        if ((el.textContent || '').trim() !== countText) {
          el.textContent = countText;
        }

        if (count > 0) {
          el.classList.remove('m:hidden');
          el.removeAttribute('hidden');
          el.style.removeProperty('display');
        } else {
          el.classList.add('m:hidden');
        }
      });
    });

    document.querySelectorAll('.m-cart-icon-bubble[aria-label]').forEach((el) => {
      if (el.getAttribute('aria-label') !== countText) {
        el.setAttribute('aria-label', countText);
      }
    });
  }

  function dispatchCartEvents(cart) {
    const detail = { cart };

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('ocu:cart:refresh', { bubbles: true, detail }));

    window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    window.dispatchEvent(new CustomEvent('ocu:cart:refresh', { detail }));

    if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
      window.Shopify.onCartUpdate(cart);
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents) {
      try {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, cart);
      } catch (error) {
        /* do nothing */
      }
    }
  }

  async function guardCart() {
    if (checking) return;
    checking = true;

    try {
      const cart = await fetchCart();

      updateCartCount(cart);
      applyBundleRemoveControls(cart);

      const counts = countBundleItemsByBundleId(cart);
      let changed = false;

      for (const item of cart.items || []) {
        if (!isGift(item)) continue;

        const bundleId = getBundleId(item);
        const matchingBundleCount = bundleId ? counts.get(bundleId) || 0 : 0;

        if (!bundleId || matchingBundleCount < CONFIG.minimumBundleItems) {
          await changeLineQuantity(item.key, 0);
          changed = true;
          continue;
        }

        if (Number(item.quantity || 0) > 1) {
          await changeLineQuantity(item.key, 1);
          changed = true;
        }
      }

      if (changed) {
        const updatedCart = await fetchCart();

        updateCartCount(updatedCart);
        dispatchCartEvents(updatedCart);

        setTimeout(() => {
          hideGiftQuantityControls();
          applyBundleRemoveControls(updatedCart);
        }, 80);

        if (window.location.pathname === '/cart') {
          window.location.reload();
        }
      }
    } catch (error) {
      console.warn('[Purist Gift Guard]', error);
    } finally {
      checking = false;
    }
  }

  function scheduleGuard(delay) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      guardCart();
      hideGiftQuantityControls();
    }, typeof delay === 'number' ? delay : CONFIG.checkDelay);
  }

  function scheduleFastGuard() {
    scheduleGuard(10);
  }

  function hasGiftText(element) {
    return element && CONFIG.giftTitlePattern.test(element.textContent || '');
  }

  function isInsideCartUi(element) {
    if (!element || !element.closest) return false;

    return Boolean(
      element.closest(
        [
          '.ocu-cart-drawer',
          'm-cart-drawer',
          '.cart-drawer',
          '#cart-drawer',
          '#CartDrawer',
          '[data-cart-drawer]',
          '[data-ocu-cart]',
          '.ocu-cart',
          '.cart-items',
          '.cart__items',
          'form[action="/cart"]',
          'form[action^="/cart"]'
        ].join(',')
      )
    );
  }

  function isInsideProductUi(element) {
    if (!element || !element.closest) return false;

    return Boolean(
      element.closest(
        [
          'purist-bundle-offers',
          '[data-section-type="product-page"]',
          '.m-main-product',
          '.m-sticky-addtocart',
          '.m-sticky-add-to-cart',
          'sticky-atc',
          'product-form',
          'form[action*="/cart/add"]'
        ].join(',')
      )
    );
  }

  function cleanupProductUiGuardAttributes() {
    document
      .querySelectorAll(
        [
          'purist-bundle-offers [data-purist-gift-line-safe]',
          '[data-section-type="product-page"] [data-purist-gift-line-safe]',
          '.m-main-product [data-purist-gift-line-safe]',
          '.m-sticky-addtocart [data-purist-gift-line-safe]',
          '.m-sticky-add-to-cart [data-purist-gift-line-safe]',
          'sticky-atc [data-purist-gift-line-safe]',
          'product-form[data-purist-gift-line-safe]',
          'form[action*="/cart/add"][data-purist-gift-line-safe]'
        ].join(',')
      )
      .forEach((element) => {
        if (isInsideCartUi(element)) return;
        element.removeAttribute('data-purist-gift-line');
        element.removeAttribute('data-purist-gift-line-safe');
      });
  }

  function cartUiRoots() {
    const selectors = [
      '.ocu-cart-drawer',
      'm-cart-drawer',
      '.cart-drawer',
      '#cart-drawer',
      '#CartDrawer',
      '[data-cart-drawer]',
      '[data-ocu-cart]',
      '.ocu-cart'
    ];

    if (window.location.pathname === '/cart') {
      selectors.push('.cart-items', '.cart__items', 'form[action="/cart"]', 'form[action^="/cart"]', '#MainContent');
    }

    return Array.from(document.querySelectorAll(selectors.join(','))).filter((root) => {
      if (!root || isInsideProductUi(root)) return false;
      return true;
    });
  }

  function findLikelyCartLine(element) {
    let node = element;

    while (node && node !== document.body) {
      const text = node.textContent || '';
      const hasGift = CONFIG.giftTitlePattern.test(text);

      const hasQtyControl =
        node.querySelector &&
        node.querySelector(
          [
            '.ocu-quantity',
            'button[aria-label*="increase" i]',
            'button[aria-label*="decrease" i]',
            'button[name="plus"]',
            'button[name="minus"]',
            'input[type="number"]',
            'input[name*="quantity" i]',
            '[data-testid*="quantity" i] button',
            '[data-testid*="quantity" i] input'
          ].join(',')
        );

      const hasPrice =
        node.querySelector &&
        node.querySelector(
          [
            '[class*="price" i]',
            '[class*="money" i]',
            '[class*="discount" i]',
            '[class*="compare" i]',
            '[class*="total" i]',
            '[data-testid*="price" i]',
            '[data-testid*="discount" i]'
          ].join(',')
        );

      /*
        Make sure we stop on the actual cart line,
        not the full drawer/body. This prevents hiding prices in other lines.
      */
      if (hasGift && hasQtyControl && hasPrice) return node;
      if (hasGift && hasQtyControl && node.className && /cart.*line|line.*item|item/i.test(String(node.className))) return node;

      node = node.parentElement;
    }

    return null;
  }

  function elementHasPriceContent(element) {
    if (!element) return false;

    const text = element.textContent || '';
    const className = typeof element.className === 'string' ? element.className : '';

    return (
      /\$\s*\d|\d+\.\d{2}|USD|CAD|AUD|GBP|EUR/i.test(text) ||
      /price|subtotal|total|discount|money|amount|compare/i.test(className) ||
      Boolean(
        element.querySelector &&
          element.querySelector(
            [
              '[class*="price" i]',
              '[class*="money" i]',
              '[class*="total" i]',
              '[class*="discount" i]',
              '[class*="compare" i]',
              '[data-testid*="price" i]',
              '[data-testid*="total" i]',
              '[data-testid*="discount" i]'
            ].join(',')
          )
      )
    );
  }

  function isPriceElement(element) {
    if (!element) return false;

    const text = element.textContent || '';
    const className = typeof element.className === 'string' ? element.className : '';

    return (
      /\$\s*\d|\d+\.\d{2}|USD|CAD|AUD|GBP|EUR/i.test(text) ||
      /price|subtotal|total|discount|money|amount|compare/i.test(className)
    );
  }

  function hideElementOnly(element) {
    if (!element) return;

    element.style.setProperty('display', 'none', 'important');
    element.style.setProperty('pointer-events', 'none', 'important');
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('tabindex', '-1');
  }

  function showElement(element) {
    if (!element) return;

    element.style.removeProperty('display');
    element.style.removeProperty('pointer-events');
    element.removeAttribute('aria-hidden');
    element.removeAttribute('tabindex');
  }

  function restorePriceElementsInside(line) {
    if (!line) return;

    const selectors = [
      '[class*="price" i]',
      '[class*="money" i]',
      '[class*="discount" i]',
      '[class*="compare" i]',
      '[class*="subtotal" i]',
      '[class*="total" i]',
      '[data-testid*="price" i]',
      '[data-testid*="discount" i]',
      '[data-testid*="total" i]'
    ];

    line.querySelectorAll(selectors.join(',')).forEach((element) => {
      /*
        Do not show the quantity wrapper back.
        Only restore price/discount/compare/total elements.
      */
      if (element.closest('.ocu-quantity')) return;

      showElement(element);
    });

    line.querySelectorAll('span, div, p, s, strong').forEach((element) => {
      if (!isPriceElement(element)) return;
      if (element.closest('.ocu-quantity')) return;

      showElement(element);
    });
  }

  function hideSafeQuantityWrapper(control, line) {
    if (!control || !line) return;

    const possibleWrapper = control.closest(
      [
        '.ocu-quantity',
        'quantity-input',
        'm-quantity-input',
        '[data-testid*="quantity" i]',
        '[class*="quantity-selector" i]',
        '[class*="quantity__" i]',
        '[class*="quantity-" i]',
        '[class*="qty-selector" i]',
        '[class*="qty__" i]',
        '[class*="qty-" i]'
      ].join(',')
    );

    if (!possibleWrapper || possibleWrapper === line) return;

    /*
      Never hide any wrapper that contains price/discount text.
    */
    if (elementHasPriceContent(possibleWrapper)) return;

    hideElementOnly(possibleWrapper);
  }

  function hideControlsInside(line) {
    if (!line) return;
    if (isInsideProductUi(line) && !isInsideCartUi(line)) return;

    /*
      Important:
      Remove old data attr from previous versions.
      Your CSS may use [data-purist-gift-line="true"] and accidentally hide prices.
    */
    line.removeAttribute('data-purist-gift-line');
    line.setAttribute('data-purist-gift-line-safe', 'true');

    restorePriceElementsInside(line);

    /*
      Hide OCU quantity wrapper only if it does NOT contain price/discount content.
      If OCU placed prices inside the same wrapper, hide only the buttons/input below.
    */
    line.querySelectorAll('.ocu-quantity').forEach((quantityWrapper) => {
      if (elementHasPriceContent(quantityWrapper)) return;
      hideElementOnly(quantityWrapper);
    });

    const controlSelectors = [
      'button[aria-label*="increase" i]',
      'button[aria-label*="decrease" i]',
      'button[name="plus"]',
      'button[name="minus"]',
      'input[type="number"]',
      'input[name*="quantity" i]',
      '[data-testid*="quantity" i] button',
      '[data-testid*="quantity" i] input'
    ];

    line.querySelectorAll(controlSelectors.join(',')).forEach((control) => {
      const label = [
        control.getAttribute('aria-label'),
        control.getAttribute('name'),
        control.getAttribute('type'),
        control.textContent,
        typeof control.className === 'string' ? control.className : ''
      ]
        .filter(Boolean)
        .join(' ');

      if (/remove|delete|trash/i.test(label)) return;

      hideElementOnly(control);
      hideSafeQuantityWrapper(control, line);
    });

    /*
      Do NOT hide broad div/span/label containers anymore.
      That was the part that could hide price and discounted price areas.
    */
    restorePriceElementsInside(line);
  }

  function hideGiftQuantityControls() {
    cleanupProductUiGuardAttributes();

    const drawers = cartUiRoots();
    if (!drawers.length) return;

    drawers.forEach((drawer) => {
      const possibleNodes = drawer.querySelectorAll('div, li, article, section, tr');

      possibleNodes.forEach((node) => {
        if (!hasGiftText(node)) return;
        if (isInsideProductUi(node) && !isInsideCartUi(node)) return;

        const line = findLikelyCartLine(node);
        if (!line) return;
        if (isInsideProductUi(line) && !isInsideCartUi(line)) return;

        hideControlsInside(line);
      });
    });
  }

  function getCartDrawerRoot() {
    return (
      document.querySelector('.ocu-cart-drawer') ||
      document.querySelector('m-cart-drawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('#cart-drawer') ||
      document.querySelector('#CartDrawer') ||
      (window.location.pathname === '/cart' ? document : null)
    );
  }

  function getRemoveButtons(line) {
    if (!line) return [];

    return Array.from(
      line.querySelectorAll(
        [
          '.ocu-cart-line-item-remove',
          '[class*="line-item-remove" i]',
          'button[aria-label*="remove" i]',
          'button[aria-label*="delete" i]',
          'a[href*="/cart/change"]'
        ].join(',')
      )
    );
  }

  function getCartLineElements() {
    const root = getCartDrawerRoot();
    if (!root) return [];

    let lines = Array.from(
      root.querySelectorAll(
        [
          '.ocu-cart-line-item',
          '[class*="ocu-cart-line-item"]:not(.ocu-cart-line-item-remove)',
          '[data-testid*="line-item" i]',
          'li',
          'article'
        ].join(',')
      )
    ).filter((line) => {
      if (!line || line === root) return false;

      const text = line.textContent || '';
      const hasRemove = getRemoveButtons(line).length > 0;
      const hasQty = Boolean(line.querySelector('.ocu-quantity, input[type="number"], [class*="quantity" i], [class*="qty" i]'));
      const hasPrice = /\$\s*\d|\d+\.\d{2}/.test(text);

      return text.trim().length > 0 && (hasRemove || hasQty || hasPrice);
    });

    if (!lines.length) {
      const removeButtons = Array.from(root.querySelectorAll('.ocu-cart-line-item-remove'));

      lines = removeButtons
        .map((button) => {
          let node = button.parentElement;

          while (node && node !== root && node !== document.body) {
            const text = node.textContent || '';
            const hasPrice = /\$\s*\d|\d+\.\d{2}/.test(text);
            const hasQty = Boolean(node.querySelector('.ocu-quantity, input[type="number"], [class*="quantity" i], [class*="qty" i]'));

            if (hasPrice || hasQty) return node;

            node = node.parentElement;
          }

          return null;
        })
        .filter(Boolean);
    }

    return Array.from(new Set(lines));
  }

  function lineMatchesCartItem(line, item) {
    if (!line || !item) return false;

    const lineText = normalizeText(line.textContent);
    const productTitle = normalizeText(item.product_title);
    const variantTitle = normalizeText(item.variant_title);
    const title = normalizeText(item.title);

    if (title && lineText.includes(title)) return true;
    if (productTitle && variantTitle && lineText.includes(productTitle) && lineText.includes(variantTitle)) return true;
    if (productTitle && lineText.includes(productTitle)) return true;

    return false;
  }

  function mapCartLinesToItems(cart) {
    const cartItems = Array.isArray(cart && cart.items) ? cart.items : [];
    const lineElements = getCartLineElements();
    const usedLines = new Set();
    const mappings = [];

    cartItems.forEach((item, itemIndex) => {
      let line = lineElements.find((candidate) => {
        if (usedLines.has(candidate)) return false;
        return lineMatchesCartItem(candidate, item);
      });

      if (!line) {
        line = lineElements.find((candidate) => !usedLines.has(candidate));
      }

      if (!line) return;

      usedLines.add(line);

      mappings.push({
        item,
        itemIndex,
        line,
        bundleId: getBundleId(item),
        role: getBundleRole(item),
        lineKey: item.key
      });
    });

    return mappings;
  }

  function resetBundleRemoveControls() {
    document.querySelectorAll('[data-purist-bundle-hidden-remove="true"], [data-purist-bundle-remove-hidden="true"]').forEach((button) => {
      button.removeAttribute('data-purist-bundle-hidden-remove');
      button.removeAttribute('data-purist-bundle-remove-hidden');
      showElement(button);
    });

    document.querySelectorAll('[data-purist-bundle-remove-all="true"]').forEach((button) => {
      button.removeAttribute('data-purist-bundle-remove-all');
      button.removeAttribute('data-purist-bundle-id');
      button.removeAttribute('title');
      button.removeAttribute('aria-label');
      showElement(button);
    });
  }

  function applyBundleRemoveControls(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    resetBundleRemoveControls();

    const mappings = mapCartLinesToItems(cart);
    const groups = new Map();

    mappings.forEach((mapping) => {
      if (!mapping.bundleId) return;

      mapping.line.setAttribute('data-purist-bundle-id', mapping.bundleId);
      mapping.line.setAttribute('data-purist-line-key', mapping.lineKey || '');

      if (!groups.has(mapping.bundleId)) groups.set(mapping.bundleId, []);
      groups.get(mapping.bundleId).push(mapping);
    });

    groups.forEach((groupMappings, bundleId) => {
      if (!groupMappings.length) return;

      groupMappings.sort((a, b) => a.itemIndex - b.itemIndex);

      const visibleRemoveMapping = groupMappings[groupMappings.length - 1];

      groupMappings.forEach((mapping) => {
        const buttons = getRemoveButtons(mapping.line);

        buttons.forEach((button) => {
          button.removeAttribute('data-purist-bundle-hidden-remove');
          button.removeAttribute('data-purist-bundle-remove-hidden');
          button.removeAttribute('data-purist-bundle-remove-all');

          if (mapping === visibleRemoveMapping) {
            showElement(button);
            button.setAttribute('data-purist-bundle-remove-all', 'true');
            button.setAttribute('data-purist-bundle-id', bundleId);
            button.setAttribute('aria-label', 'Remove bundle');
            button.setAttribute('title', 'Remove bundle');
          } else {
            button.setAttribute('data-purist-bundle-hidden-remove', 'true');
            button.setAttribute('data-purist-bundle-remove-hidden', 'true');
            hideElementOnly(button);
          }
        });
      });
    });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function hideBundleLinesInDom(bundleId) {
    if (!bundleId) return;

    document.querySelectorAll(`[data-purist-bundle-id="${cssEscape(bundleId)}"]`).forEach((line) => {
      line.style.setProperty('opacity', '0.35', 'important');
      line.style.setProperty('pointer-events', 'none', 'important');
    });
  }

  async function removeFullBundle(bundleId) {
    if (!bundleId || removingBundle) return;

    removingBundle = true;
    hideBundleLinesInDom(bundleId);

    try {
      const cart = await fetchCart();
      const updates = {};

      (cart.items || []).forEach((item) => {
        if (getBundleId(item) === bundleId && item.key) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return;

      let updatedCart;

      try {
        updatedCart = await updateCartLines(updates);
      } catch (error) {
        for (const key of Object.keys(updates)) {
          await changeLineQuantity(key, 0);
        }

        updatedCart = await fetchCart();
      }

      updateCartCount(updatedCart);
      dispatchCartEvents(updatedCart);

      document.querySelectorAll(`[data-purist-bundle-id="${cssEscape(bundleId)}"]`).forEach((line) => {
        line.remove();
      });

      setTimeout(() => {
        scheduleFastGuard();
      }, 50);

      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    } catch (error) {
      console.warn('[Purist Gift Guard] Unable to remove bundle:', error);
    } finally {
      removingBundle = false;
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const removeButton = target.closest('[data-purist-bundle-remove-all="true"]');
      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      const bundleId =
        removeButton.getAttribute('data-purist-bundle-id') ||
        removeButton.closest('[data-purist-bundle-id]')?.getAttribute('data-purist-bundle-id') ||
        '';

      removeFullBundle(bundleId);
    },
    true
  );

  function checkoutSelector() {
    return [
      'a[href="/checkout"]',
      'a[href^="/checkout?"]',
      'form[action="/checkout"]',
      'form[action^="/checkout"]',
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[data-testid*="checkout" i]',
      '.ocu-checkout',
      '.ocu-checkout-button',
      '.ocu-cart-checkout'
    ].join(',');
  }

  let allowCheckout = false;

  document.addEventListener(
    'click',
    async function (event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkout = target.closest(checkoutSelector());
      if (!checkout || allowCheckout) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      await guardCart();

      allowCheckout = true;

      const form = checkout.closest && checkout.closest('form[action*="/checkout"]');

      if (form) {
        form.submit();
      } else if (checkout.tagName === 'A' && checkout.href) {
        window.location.href = checkout.href;
      } else {
        window.location.href = rootUrl() + 'checkout';
      }

      setTimeout(() => {
        allowCheckout = false;
      }, 1500);
    },
    true
  );

  document.addEventListener(
    'submit',
    async function (event) {
      const form = event.target instanceof Element ? event.target : null;
      if (!form || !form.matches('form[action*="/checkout"]') || allowCheckout) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      await guardCart();

      allowCheckout = true;
      form.submit();

      setTimeout(() => {
        allowCheckout = false;
      }, 1500);
    },
    true
  );

  [
    'cart:refresh',
    'cart:updated',
    'cart:update',
    'cart:change',
    'ocu:cart:refresh',
    'ajaxProduct:added'
  ].forEach((eventName) => {
    document.addEventListener(eventName, scheduleFastGuard);
    window.addEventListener(eventName, scheduleFastGuard);
  });

  const observedCartUiSelector = [
    '.ocu-cart-drawer',
    'm-cart-drawer',
    '.cart-drawer',
    '#cart-drawer',
    '#CartDrawer',
    '[data-cart-drawer]',
    '[data-ocu-cart]',
    '.ocu-cart',
    '.cart-items',
    '.cart__items',
    'form[action="/cart"]',
    'form[action^="/cart"]'
  ].join(',');

  const observedCartCountSelector = [
    '.m-cart-count-bubble',
    '.m-cart-count',
    '[data-cart-count]',
    '.cart-count-bubble',
    '.cart-count',
    '[data-testid="cart-count"]',
    '.ocu-cart-header--items-count'
  ].join(',');

  function mutationElement(node) {
    if (!node) return null;
    if (node.nodeType === Node.ELEMENT_NODE) return node;
    return node.parentElement || null;
  }

  function nodeContainsCartUi(node) {
    const element = mutationElement(node);
    if (!element) return false;

    return Boolean(
      element.matches?.(observedCartUiSelector) ||
      element.querySelector?.(observedCartUiSelector)
    );
  }

  function mutationAffectsCartUi(mutation) {
    if (mutation.type !== 'childList') return false;

    const target = mutationElement(mutation.target);
    if (!target) return false;

    /*
      Ignore cart-count text changes made by updateCartCount(). These are guard
      output, not a new cart change, and must not schedule another /cart.js call.
    */
    if (target.closest?.(observedCartCountSelector)) return false;

    /*
      React only to actual cart drawer/cart-page DOM changes. Mobile menus,
      accordions, sliders and other page interactions must not run the guard.
    */
    if (
      target.matches?.(observedCartUiSelector) ||
      target.closest?.(observedCartUiSelector)
    ) {
      return true;
    }

    const changedNodes = [
      ...Array.from(mutation.addedNodes || []),
      ...Array.from(mutation.removedNodes || [])
    ];

    return changedNodes.some(nodeContainsCartUi);
  }

  const observer = new MutationObserver((mutations) => {
    if (mutations.some(mutationAffectsCartUi)) {
      scheduleGuard(100);
    }
  });

  function start() {
    /*
      Clean old attribute from previous guard versions.
      This prevents CSS like [data-purist-gift-line="true"] [class*="qty"]
      from hiding price or discount price.
    */
    document.querySelectorAll('[data-purist-gift-line="true"]').forEach((line) => {
      line.removeAttribute('data-purist-gift-line');
    });

    cleanupProductUiGuardAttributes();
    hideGiftQuantityControls();
    scheduleFastGuard();

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.PuristGiftGuard = {
    guardCart,
    hideGiftQuantityControls,
    applyBundleRemoveControls,
    removeFullBundle,
    updateCartCount,
    scheduleGuard,
    scheduleFastGuard
  };
})();